/**
 * Roki Overtime — cart drawer shipping estimate (v3)
 * File: assets/shipping-estimate.js
 * Markup: snippets/cart-drawer__shipping-estimate.liquid
 *
 * What it does
 * - Asks Shopify's Cart API for the shipping rate on the current cart
 *   (prepare_shipping_rates, then async_shipping_rates). These are the same
 *   rates checkout offers, so mixed-supplier carts show the combined rate.
 * - Shows the cheapest rate plus an estimated total (subtotal + shipping).
 * - Only runs while the cart drawer is open, and remembers results so
 *   re-opening the drawer doesn't repeat the lookup.
 * - Countries where Shopify needs a postcode (see COUNTRIES below): the
 *   shopper enters it once and it's remembered on their device. Where
 *   Shopify also needs a state/province, it's worked out from the postcode
 *   (AU, US, CA, ES) or picked from a list (India).
 * - Other countries: tries the shopper's country on its own (e.g. Israel).
 * - Rates quoted in the store currency (AUD) are converted to the shopper's
 *   currency with Shopify's live exchange rate.
 * - Anywhere it can't get a rate, it shows "Calculated at checkout".
 *   Australian carts with a RokiHQ item land here: Australia Post needs a
 *   full street address, which only checkout collects.
 *
 * Changes
 * v2   US ZIP codes; "Change postcode" hidden when the postcode isn't the
 *      reason there's no rate.
 * v2.1 Live exchange-rate conversion (a US shopper saw "$6.95" = 6.95 AUD
 *      instead of $5.05).
 * v3   Canada, UK, Germany, France, Singapore, Spain and India.
 */
(function () {
  if (customElements.get('shipping-estimate')) return;

  const POLL_DELAYS_MS = [300, 500, 800, 1200, 1600, 2200]; // ~6.6s before giving up
  const cache = window.__rokiShipCache || (window.__rokiShipCache = new Map());
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /* ------------------------------------------------------------------ */
  /* Postcode -> state/province lookups.                                 */
  /* Names match Shopify's own province list exactly.                    */
  /* ------------------------------------------------------------------ */

  // Australia: 4-digit postcode ranges.
  function auState(postcode) {
    const n = parseInt(postcode, 10);
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) {
      return 'Australian Capital Territory';
    }
    if (n >= 800 && n <= 999) return 'Northern Territory';
    if (n >= 1000 && n <= 2999) return 'New South Wales';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'Victoria';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'Queensland';
    if (n >= 5000 && n <= 5999) return 'South Australia';
    if (n >= 6000 && n <= 6999) return 'Western Australia';
    if (n >= 7000 && n <= 7999) return 'Tasmania';
    return '';
  }

  // United States: first three digits of the ZIP (USPS prefix ranges).
  // Territories and military addresses aren't covered.
  const US_ZIP_RANGES = [
    [5, 5, 'New York'], [10, 27, 'Massachusetts'], [28, 29, 'Rhode Island'],
    [30, 38, 'New Hampshire'], [39, 49, 'Maine'], [50, 54, 'Vermont'],
    [55, 55, 'Massachusetts'], [56, 59, 'Vermont'], [60, 69, 'Connecticut'],
    [70, 89, 'New Jersey'], [100, 149, 'New York'], [150, 196, 'Pennsylvania'],
    [197, 199, 'Delaware'], [200, 200, 'District of Columbia'], [201, 201, 'Virginia'],
    [202, 205, 'District of Columbia'], [206, 219, 'Maryland'], [220, 246, 'Virginia'],
    [247, 268, 'West Virginia'], [270, 289, 'North Carolina'], [290, 299, 'South Carolina'],
    [300, 319, 'Georgia'], [320, 339, 'Florida'], [341, 349, 'Florida'],
    [350, 369, 'Alabama'], [370, 385, 'Tennessee'], [386, 397, 'Mississippi'],
    [398, 399, 'Georgia'], [400, 427, 'Kentucky'], [430, 459, 'Ohio'],
    [460, 479, 'Indiana'], [480, 499, 'Michigan'], [500, 528, 'Iowa'],
    [530, 549, 'Wisconsin'], [550, 567, 'Minnesota'], [569, 569, 'District of Columbia'],
    [570, 577, 'South Dakota'], [580, 588, 'North Dakota'], [590, 599, 'Montana'],
    [600, 629, 'Illinois'], [630, 658, 'Missouri'], [660, 679, 'Kansas'],
    [680, 693, 'Nebraska'], [700, 715, 'Louisiana'], [716, 729, 'Arkansas'],
    [730, 732, 'Oklahoma'], [733, 733, 'Texas'], [734, 749, 'Oklahoma'],
    [750, 799, 'Texas'], [800, 816, 'Colorado'], [820, 831, 'Wyoming'],
    [832, 838, 'Idaho'], [840, 847, 'Utah'], [850, 865, 'Arizona'],
    [870, 884, 'New Mexico'], [885, 885, 'Texas'], [889, 898, 'Nevada'],
    [900, 961, 'California'], [967, 968, 'Hawaii'], [970, 979, 'Oregon'],
    [980, 994, 'Washington'], [995, 999, 'Alaska'],
  ];
  function usState(zip) {
    const prefix = parseInt(zip.slice(0, 3), 10);
    const match = US_ZIP_RANGES.find(([from, to]) => prefix >= from && prefix <= to);
    return match ? match[2] : '';
  }

  // Canada: the first letter of the postal code identifies the province.
  const CA_FIRST_LETTER = {
    A: 'Newfoundland and Labrador', B: 'Nova Scotia', C: 'Prince Edward Island',
    E: 'New Brunswick', G: 'Quebec', H: 'Quebec', J: 'Quebec',
    K: 'Ontario', L: 'Ontario', M: 'Ontario', N: 'Ontario', P: 'Ontario',
    R: 'Manitoba', S: 'Saskatchewan', T: 'Alberta', V: 'British Columbia',
    Y: 'Yukon',
  };
  function caProvince(postal) {
    const first = postal.charAt(0);
    if (first === 'X') {
      // X0A, X0B, X0C are Nunavut; the rest of X is the Northwest Territories.
      return /^X0[ABC]/.test(postal) ? 'Nunavut' : 'Northwest Territories';
    }
    return CA_FIRST_LETTER[first] || '';
  }

  // Spain: the first two digits of the postal code are the province number.
  const ES_PROVINCES = [
    '', 'Álava', 'Albacete', 'Alicante', 'Almería', 'Ávila', 'Badajoz', 'Balears',
    'Barcelona', 'Burgos', 'Cáceres', 'Cádiz', 'Castellón', 'Ciudad Real', 'Córdoba',
    'A Coruña', 'Cuenca', 'Girona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva',
    'Huesca', 'Jaén', 'León', 'Lleida', 'La Rioja', 'Lugo', 'Madrid', 'Málaga',
    'Murcia', 'Navarra', 'Ourense', 'Asturias', 'Palencia', 'Las Palmas', 'Pontevedra',
    'Salamanca', 'Santa Cruz de Tenerife', 'Cantabria', 'Segovia', 'Sevilla', 'Soria',
    'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid', 'Vizcaya', 'Zamora',
    'Zaragoza', 'Ceuta', 'Melilla',
  ];
  function esProvince(postal) {
    return ES_PROVINCES[parseInt(postal.slice(0, 2), 10)] || '';
  }

  // India: PIN codes don't map cleanly to states, so the shopper picks one.
  const IN_STATES = [
    'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
    'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli', 'Daman and Diu', 'Delhi', 'Goa',
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
    'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
    'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  ];

  /* ------------------------------------------------------------------ */
  /* Postcode formats. Each returns the tidied postcode, or '' if it     */
  /* isn't valid.                                                        */
  /* ------------------------------------------------------------------ */
  const digits = (count) => (raw) => {
    const value = raw.replace(/\s+/g, '');
    return new RegExp(`^\\d{${count}}$`).test(value) ? value : '';
  };

  function ukPostcode(raw) {
    const match = raw.toUpperCase().replace(/\s+/g, '').match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/);
    return match ? `${match[1]} ${match[2]}` : '';
  }

  function caPostal(raw) {
    const value = raw.toUpperCase().replace(/\s+/g, '');
    return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/.test(value)
      ? `${value.slice(0, 3)} ${value.slice(3)}`
      : '';
  }

  /* ------------------------------------------------------------------ */
  /* Per-country settings. Codes must match the list in the snippet.     */
  /* province: function(postcode) -> state name, 'pick' for a list, or   */
  /* null when Shopify doesn't need one.                                 */
  /* ------------------------------------------------------------------ */
  const COUNTRIES = {
    au: {
      country: 'Australia', label: 'Postcode', noun: 'postcode', maxLength: 4, numeric: true,
      clean: (raw) => { const v = digits(4)(raw); return v && auState(v) ? v : ''; },
      province: auState,
      storageKey: 'roki_ship_postcode', // same key as v1, so saved postcodes carry over
      invalid: 'Enter a 4-digit Australian postcode.',
    },
    us: {
      country: 'United States', label: 'ZIP code', noun: 'ZIP code', maxLength: 5, numeric: true,
      clean: (raw) => { const v = digits(5)(raw); return v && usState(v) ? v : ''; },
      province: usState,
      invalid: 'Enter a 5-digit ZIP code in the 50 states or DC.',
    },
    ca: {
      country: 'Canada', label: 'Postal code', noun: 'postal code', maxLength: 7, numeric: false,
      clean: caPostal, province: caProvince,
      invalid: 'Enter a postal code like M5V 3L9.',
    },
    gb: {
      country: 'United Kingdom', label: 'Postcode', noun: 'postcode', maxLength: 8, numeric: false,
      clean: ukPostcode, province: null,
      invalid: 'Enter a UK postcode like SW1A 1AA.',
    },
    de: {
      country: 'Germany', label: 'Postal code', noun: 'postal code', maxLength: 5, numeric: true,
      clean: digits(5), province: null,
      invalid: 'Enter a 5-digit postal code.',
    },
    fr: {
      country: 'France', label: 'Postal code', noun: 'postal code', maxLength: 5, numeric: true,
      clean: digits(5), province: null,
      invalid: 'Enter a 5-digit postal code.',
    },
    sg: {
      country: 'Singapore', label: 'Postal code', noun: 'postal code', maxLength: 6, numeric: true,
      clean: digits(6), province: null,
      invalid: 'Enter a 6-digit postal code.',
    },
    es: {
      country: 'Spain', label: 'Postal code', noun: 'postal code', maxLength: 5, numeric: true,
      clean: (raw) => { const v = digits(5)(raw); return v && esProvince(v) ? v : ''; },
      province: esProvince,
      invalid: 'Enter a 5-digit Spanish postal code.',
    },
    in: {
      country: 'India', label: 'PIN code', noun: 'PIN code', maxLength: 6, numeric: true,
      clean: (raw) => { const v = digits(6)(raw); return /^[1-9]/.test(v) ? v : ''; },
      province: 'pick', states: IN_STATES,
      invalid: 'Enter a 6-digit PIN code.',
      invalidState: 'Select your state.',
    },
  };

  const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

  function readSaved(key) {
    try {
      return localStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  }

  function save(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* storage blocked (e.g. private mode) — still works, just not remembered */
    }
  }

  function formatMoney(cents, currency, withCode) {
    const amount = cents / 100;
    let text;
    try {
      text = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (e) {
      text = amount.toFixed(2);
    }
    return withCode ? `${text} ${currency}` : text;
  }

  /*
   * Converts an amount quoted in the store currency (AUD) into the shopper's
   * currency. It uses window.Shopify.currency.rate, which Shopify puts on
   * every page: the same exchange rate it uses for the shopper's product
   * prices and at checkout. Your markets use automatic rates, which Shopify
   * updates through the day, so this follows the market.
   * Returns null if it can't convert safely.
   */
  function toCartCurrency(cents, fromCurrency, cartCurrency, shopCurrency) {
    if (fromCurrency === cartCurrency) return cents;
    const live = window.Shopify && window.Shopify.currency;
    const rate = live ? parseFloat(live.rate) : NaN;
    if (fromCurrency === shopCurrency && live && live.active === cartCurrency && rate > 0) {
      // toFixed(6) first so floating-point noise (486.4999…) rounds like the maths (486.5 → 487)
      return Math.round(Number((cents * rate).toFixed(6)));
    }
    return null;
  }

  function buildQuery(address) {
    const params = new URLSearchParams();
    params.append('shipping_address[country]', address.country);
    if (address.province) params.append('shipping_address[province]', address.province);
    if (address.zip) params.append('shipping_address[zip]', address.zip);
    return params.toString();
  }

  async function fetchRates(address) {
    const query = buildQuery(address);

    const prepare = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!prepare.ok) {
      const error = new Error('prepare_failed');
      error.status = prepare.status;
      throw error;
    }

    for (const delay of POLL_DELAYS_MS) {
      await sleep(delay);
      const response = await fetch(`${root}cart/async_shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' },
      });
      if (response.status === 202) continue; // still calculating
      if (!response.ok) {
        const error = new Error('async_failed');
        error.status = response.status;
        throw error;
      }
      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }
      if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      // null means Shopify hasn't finished yet — poll again
    }

    throw new Error('timeout');
  }

  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      // Free shipping is already rendered by Liquid — nothing to fetch.
      if (this.dataset.free === 'true') return;

      this.valueEl = this.querySelector('[data-ship-value]');
      this.form = this.querySelector('[data-ship-form]');
      this.zipInput = this.querySelector('[data-ship-zip]');
      this.zipLabel = this.querySelector('[data-ship-zip-label]');
      this.stateSelect = this.querySelector('[data-ship-state]');
      this.meta = this.querySelector('[data-ship-meta]');
      this.metaText = this.querySelector('[data-ship-meta-text]');
      this.changeButton = this.querySelector('[data-ship-change]');
      this.totalRow = this.querySelector('[data-ship-total-row]');
      this.totalEl = this.querySelector('[data-ship-total]');
      if (!this.valueEl || !this.form || !this.meta || !this.totalRow) return;

      const code = this.dataset.zipMode;
      this.mode = COUNTRIES[code] || null; // null = country-only lookup
      if (this.mode) {
        this.zipKey = this.mode.storageKey || `roki_ship_zip_${code}`;
        this.stateKey = `roki_ship_state_${code}`;
        this.setUpForm();
      }
      this.started = false;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.onZipSubmit();
      });
      this.changeButton.addEventListener('click', () => this.showForm(''));

      // Wait until the drawer is actually open before calling Shopify.
      this.drawer = this.closest('cart-drawer');
      if (this.drawer) {
        this.observer = new MutationObserver(() => this.startIfOpen());
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }
      this.startIfOpen();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    // Labels, input type and (India) the state list for this country.
    setUpForm() {
      const mode = this.mode;
      if (this.zipLabel) this.zipLabel.textContent = mode.label;
      this.zipInput.maxLength = mode.maxLength;
      this.zipInput.inputMode = mode.numeric ? 'numeric' : 'text';
      this.changeButton.textContent = `Change ${mode.noun}`;

      if (mode.province === 'pick' && this.stateSelect) {
        const saved = readSaved(this.stateKey);
        const placeholder = new Option('Select state', '');
        placeholder.disabled = true;
        this.stateSelect.replaceChildren(placeholder, ...mode.states.map((name) => new Option(name, name)));
        this.stateSelect.value = mode.states.includes(saved) ? saved : '';
        this.stateSelect.hidden = false;
      }
    }

    startIfOpen() {
      if (this.started) return;
      if (this.drawer && !this.drawer.classList.contains('active')) return;
      this.started = true;
      if (this.observer) this.observer.disconnect();
      this.start();
    }

    start() {
      if (!this.mode) {
        this.estimate(null, null);
        return;
      }
      const zip = this.mode.clean(readSaved(this.zipKey));
      const province = zip ? this.provinceFor(zip) : '';
      if (zip && province !== '') {
        this.estimate(zip, province);
      } else {
        this.showForm('');
      }
    }

    // State/province Shopify needs for this postcode: a name, null if none is needed, or '' if missing.
    provinceFor(zip) {
      const rule = this.mode.province;
      if (!rule) return null;
      if (rule === 'pick') return this.stateSelect ? this.stateSelect.value : '';
      return rule(zip);
    }

    onZipSubmit() {
      const zip = this.mode.clean(this.zipInput.value || '');
      if (!zip) {
        this.showForm(this.mode.invalid);
        return;
      }
      const province = this.provinceFor(zip);
      if (province === '') {
        this.showForm(this.mode.invalidState || this.mode.invalid);
        return;
      }
      save(this.zipKey, zip);
      if (this.mode.province === 'pick') save(this.stateKey, province);
      this.estimate(zip, province);
    }

    setValue(text, tone) {
      const span = document.createElement('span');
      if (tone) span.className = `ship-est__${tone}`;
      span.textContent = text;
      this.valueEl.replaceChildren(span);
    }

    setMeta(text, isError, showChange) {
      this.metaText.textContent = text;
      this.metaText.classList.toggle('ship-est__error', Boolean(isError));
      this.changeButton.hidden = !showChange;
      this.meta.hidden = !text && !showChange;
    }

    hideTotal() {
      this.totalRow.hidden = true;
    }

    showForm(errorText) {
      this.setValue(`Enter your ${this.mode.noun}`, 'muted');
      this.hideTotal();
      this.form.hidden = false;
      this.setMeta(errorText, Boolean(errorText), false);
      const saved = readSaved(this.zipKey);
      if (saved && !this.zipInput.value) this.zipInput.value = saved;
    }

    async estimate(zip, province) {
      const address = this.mode
        ? { country: this.mode.country, province: province || '', zip: zip }
        : { country: this.dataset.countryName };
      const cacheKey = [this.dataset.cartSignature, address.country, address.province || '', address.zip || ''].join('|');

      this.form.hidden = true;
      this.setMeta('', false, false);
      this.setValue('Checking…', 'muted');

      let result = cache.get(cacheKey);
      if (!result) {
        try {
          result = { rates: await fetchRates(address) };
          cache.set(cacheKey, result); // only successes are cached
        } catch (error) {
          result = { error };
        }
      }

      if (!this.isConnected) return; // drawer re-rendered while we waited
      this.render(result, zip);
    }

    render(result, zip) {
      const rates = (result.rates || []).filter((rate) => rate && !Number.isNaN(parseFloat(rate.price)));

      if (!rates.length) {
        // Shopify rejected the postcode itself.
        if (this.mode && result.error && result.error.status === 422) {
          this.showForm(`${capitalise(this.mode.noun)} not recognised. Check it and try again.`);
          return;
        }
        this.setValue('Calculated at checkout', 'muted');
        this.hideTotal();
        // Empty result = the postcode was fine, but a rate needs checkout's
        // full address (e.g. Australia Post), so don't suggest changing it.
        // Timeouts and other errors keep the option to try again.
        this.setMeta('', false, Boolean(this.mode && result.error));
        return;
      }

      const cheapest = rates.reduce((a, b) => (parseFloat(b.price) < parseFloat(a.price) ? b : a));
      const quotedCents = Math.round(parseFloat(cheapest.price) * 100);
      const currency = this.dataset.currency; // the shopper's currency
      const shippingCents = toCartCurrency(
        quotedCents,
        cheapest.currency || currency,
        currency,
        this.dataset.shopCurrency
      );

      // Never show an amount in the wrong currency.
      if (shippingCents === null) {
        this.setValue('Calculated at checkout', 'muted');
        this.hideTotal();
        this.setMeta('', false, Boolean(this.mode));
        return;
      }

      if (shippingCents === 0) {
        this.setValue('Free', 'free');
      } else {
        this.setValue(formatMoney(shippingCents, currency, false), '');
      }

      const where = this.mode ? zip : this.dataset.countryName;
      this.setMeta(`Estimated to ${where}`, false, Boolean(this.mode));

      const subtotalCents = parseInt(this.dataset.subtotalCents, 10) || 0;
      this.totalEl.textContent = formatMoney(subtotalCents + shippingCents, currency, true);
      this.totalRow.hidden = false;
    }
  }

  customElements.define('shipping-estimate', ShippingEstimate);
})();
